import { useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';

import css from './index.module.less';

/**
 * 打印效果类型
 */
export type TypeWriterEffect = 'normal' | 'fade' | 'blur' | 'scale' | 'slide' | 'glitch';

/**
 * 光标样式类型
 */
export type CursorStyle = 'line' | 'block' | 'underscore' | 'none';

/**
 * 内容项类型 - 支持文字和图片混排
 */
export type TypeWriterContentItem =
  | {
      type: 'text';
      content: string;
    }
  | {
      type: 'image';
      src: string;
      alt?: string;
      width?: number | string;
      height?: number | string;
      className?: string;
      style?: React.CSSProperties;
    }
  | {
      /** 换行 */
      type: 'newline';
    };

export interface TypeWriterProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'content'> {
  /** 要打印的内容，支持文字和图片混排 */
  content: TypeWriterContentItem[];
  /** 每个字符的打印速度(ms)，默认为 80 */
  speed?: number;
  /** 图片显示的速度(ms)，默认为 300 */
  imageSpeed?: number;
  /** 初始延迟时间(ms)，默认为 0 */
  delay?: number;
  /** 打字效果类型，默认为 'normal' */
  effect?: TypeWriterEffect;
  /** 字符动画持续时间(ms)，默认为 200 */
  charAnimationDuration?: number;
  /** 光标样式，默认为 'line' */
  cursorStyle?: CursorStyle;
  /** 光标闪烁速度(ms)，默认为 500 */
  cursorBlinkSpeed?: number;
  /** 是否在打字完成后隐藏光标，默认为 true */
  hideCursorOnComplete?: boolean;
  /** 是否循环打印，默认为 false */
  loop?: boolean;
  /** 循环时的暂停时间(ms)，默认为 2000 */
  loopPauseTime?: number;
  /** 是否支持删除效果（循环时），默认为 true */
  deleteOnLoop?: boolean;
  /** 删除速度(ms)，默认为 40 */
  deleteSpeed?: number;
  /** 是否只在视口内时触发，默认为 true */
  triggerOnView?: boolean;
  /** 进入视口的阈值，默认为 1（完全可见时触发） */
  threshold?: number;
  /** 是否只触发一次，默认为 true */
  once?: boolean;
  /** 打字完成时的回调 */
  onComplete?: () => void;
  /** 每次循环完成时的回调 */
  onLoopComplete?: () => void;
  /** 随机速度变化范围(ms)，默认为 0 */
  randomSpeed?: number;
  /** 是否暂停打印 */
  paused?: boolean;
}

// 扁平化内容项
interface FlattenedItem {
  type: 'char' | 'image' | 'newline';
  value: string;
  originalIndex: number;
  charIndex?: number;
  imageProps?: TypeWriterContentItem & { type: 'image' };
}

/**
 * 将内容数组扁平化为单个字符/图片/换行的数组
 */
function flattenContent(content: TypeWriterContentItem[]): FlattenedItem[] {
  const result: FlattenedItem[] = [];
  content.forEach((item, originalIndex) => {
    if (item.type === 'text') {
      item.content.split('').forEach((char, charIndex) => {
        result.push({ type: 'char', value: char, originalIndex, charIndex });
      });
    } else if (item.type === 'image') {
      result.push({ type: 'image', value: item.src, originalIndex, imageProps: item });
    } else if (item.type === 'newline') {
      result.push({ type: 'newline', value: '', originalIndex });
    }
  });
  return result;
}

/**
 * TypeWriter - 文字打印机组件
 */
export function TypeWriter({
  content,
  speed = 80,
  imageSpeed = 300,
  delay = 0,
  effect = 'normal',
  charAnimationDuration = 200,
  cursorStyle = 'line',
  cursorBlinkSpeed = 500,
  hideCursorOnComplete = true,
  loop = false,
  loopPauseTime = 2000,
  deleteOnLoop = true,
  deleteSpeed = 40,
  triggerOnView = true,
  threshold = 1,
  once = true,
  onComplete,
  onLoopComplete,
  randomSpeed = 0,
  paused = false,
  className,
  style,
  ...restProps
}: TypeWriterProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // 将内容扁平化
  const flattenedContent = useMemo(() => flattenContent(content), [content]);

  // 使用 ref 存储所有可变状态和 props，避免依赖变化导致函数重建
  const stateRef = useRef({
    currentIndex: 0,
    isDeleting: false,
    isComplete: false,
    hasTriggered: false,
    isInView: !triggerOnView,
    timer: null as ReturnType<typeof setTimeout> | null,
  });

  const propsRef = useRef({
    speed,
    imageSpeed,
    deleteSpeed,
    loop,
    deleteOnLoop,
    loopPauseTime,
    paused,
    randomSpeed,
    onComplete,
    onLoopComplete,
    once,
    delay,
    triggerOnView,
    threshold,
  });

  // 每次渲染时同步更新 propsRef
  propsRef.current = {
    speed,
    imageSpeed,
    deleteSpeed,
    loop,
    deleteOnLoop,
    loopPauseTime,
    paused,
    randomSpeed,
    onComplete,
    onLoopComplete,
    once,
    delay,
    triggerOnView,
    threshold,
  };

  const flattenedContentRef = useRef(flattenedContent);
  flattenedContentRef.current = flattenedContent;

  // 用于追踪内容变化（首次挂载 vs 后续更新）
  const isFirstMount = useRef(true);
  const prevContentRef = useRef(content);

  // 清除定时器 - 使用稳定引用
  const clearTimer = () => {
    if (stateRef.current.timer) {
      clearTimeout(stateRef.current.timer);
      stateRef.current.timer = null;
    }
  };

  // 获取随机速度
  const getRandomSpeed = (baseSpeed: number) => {
    const rs = propsRef.current.randomSpeed;
    if (rs === 0) return baseSpeed;
    const variation = Math.random() * rs * 2 - rs;
    return Math.max(10, baseSpeed + variation);
  };

  // 打字逻辑 - 核心 tick 函数
  const tick = () => {
    const props = propsRef.current;
    const state = stateRef.current;
    const flatContent = flattenedContentRef.current;

    if (props.paused) return;

    if (state.isDeleting) {
      if (state.currentIndex > 0) {
        state.currentIndex -= 1;
        setDisplayIndex(state.currentIndex);
        state.timer = setTimeout(tick, getRandomSpeed(props.deleteSpeed));
      } else {
        state.isDeleting = false;
        state.timer = setTimeout(tick, props.loopPauseTime);
      }
    } else {
      if (state.currentIndex < flatContent.length) {
        const currentItem = flatContent[state.currentIndex];
        const itemSpeed = currentItem.type === 'image' ? props.imageSpeed : props.speed;
        state.currentIndex += 1;
        setDisplayIndex(state.currentIndex);
        state.timer = setTimeout(tick, getRandomSpeed(itemSpeed));
      } else {
        // 打字完成
        if (!state.isComplete) {
          state.isComplete = true;
          setIsComplete(true);
          props.onComplete?.();
        }

        if (props.loop) {
          props.onLoopComplete?.();
          if (props.deleteOnLoop) {
            state.timer = setTimeout(() => {
              state.isDeleting = true;
              tick();
            }, props.loopPauseTime);
          } else {
            state.timer = setTimeout(() => {
              state.currentIndex = 0;
              state.isDeleting = false;
              state.isComplete = false;
              setIsComplete(false);
              setDisplayIndex(0);
              tick();
            }, props.loopPauseTime);
          }
        }
      }
    }
  };

  // 开始打字
  const startTyping = () => {
    const state = stateRef.current;
    const props = propsRef.current;

    if (props.once && state.hasTriggered) return;

    state.hasTriggered = true;
    clearTimer();
    state.currentIndex = 0;
    state.isDeleting = false;
    state.isComplete = false;
    setIsComplete(false);
    setDisplayIndex(0);

    state.timer = setTimeout(tick, props.delay);
  };

  // 重置到完成状态（用于内容切换时跳过动画）
  const skipToComplete = () => {
    clearTimer();
    const length = flattenedContentRef.current.length;
    stateRef.current.currentIndex = length;
    stateRef.current.isComplete = true;
    stateRef.current.isDeleting = false;
    setIsComplete(true);
    setDisplayIndex(length);
    propsRef.current.onComplete?.();
  };

  // 内容变化处理：首次展示动画，后续直接显示完整内容
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      prevContentRef.current = content;
      return;
    }

    // 判断内容是否真正变化（而非仅引用变化）
    if (prevContentRef.current === content) {
      return;
    }
    prevContentRef.current = content;

    // 内容变化时直接展示完整结果
    skipToComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  // Intersection Observer - 视口触发
  useEffect(() => {
    if (!triggerOnView) return;

    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const props = propsRef.current;
          const state = stateRef.current;

          if (entry.intersectionRatio >= props.threshold) {
            state.isInView = true;
            if (!state.hasTriggered) {
              startTyping();
            }
          } else if (entry.intersectionRatio === 0) {
            state.isInView = false;
            if (!props.once) {
              clearTimer();
              state.currentIndex = 0;
              state.isDeleting = false;
              state.isComplete = false;
              state.hasTriggered = false;
              setIsComplete(false);
              setDisplayIndex(0);
            }
          }
        });
      },
      { threshold: [0, threshold] },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerOnView, threshold]);

  // 非视口触发模式，组件挂载时直接开始
  useEffect(() => {
    if (!triggerOnView && !stateRef.current.hasTriggered) {
      startTyping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerOnView]);

  // 暂停/恢复
  useEffect(() => {
    if (paused) {
      clearTimer();
    } else if (
      stateRef.current.isInView &&
      stateRef.current.hasTriggered &&
      !stateRef.current.isComplete
    ) {
      stateRef.current.timer = setTimeout(tick, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => clearTimer();
  }, []);

  // 渲染
  const visibleContent = flattenedContent.slice(0, displayIndex);
  const showCursor = cursorStyle !== 'none' && !(isComplete && hideCursorOnComplete);

  return (
    <div
      ref={containerRef}
      className={clsx(css['typewriter'], className)}
      style={style}
      {...restProps}
    >
      <span className={css['typewriter-content']}>
        {visibleContent.map((item, index) => {
          const isLatest = index === displayIndex - 1;

          if (item.type === 'char') {
            return (
              <span
                key={`${item.originalIndex}-${item.charIndex}`}
                className={clsx(css['typewriter-char'], css[`effect-${effect}`], {
                  [css['latest']]: isLatest,
                  [css['space']]: item.value === ' ',
                })}
                style={{ animationDuration: `${charAnimationDuration}ms` }}
              >
                {item.value}
              </span>
            );
          }

          if (item.type === 'newline') {
            return <br key={`nl-${item.originalIndex}-${index}`} />;
          }

          const imgProps = item.imageProps!;
          return (
            <span
              key={`img-${item.originalIndex}`}
              className={clsx(css['typewriter-image'], css[`effect-${effect}`], {
                [css['latest']]: isLatest,
              })}
              style={{ animationDuration: `${charAnimationDuration}ms` }}
            >
              <img
                src={imgProps.src}
                alt={imgProps.alt || ''}
                width={imgProps.width}
                height={imgProps.height}
                className={imgProps.className}
                style={imgProps.style}
              />
            </span>
          );
        })}
        {showCursor && (
          <span
            className={clsx(css['typewriter-cursor'], css[`cursor-${cursorStyle}`])}
            style={{ animationDuration: `${cursorBlinkSpeed}ms` }}
          />
        )}
      </span>
    </div>
  );
}

export default TypeWriter;
